import type { AuthStatus, GetScanJobResponse } from '@purebox/shared';

export type ScanJobData = Pick<GetScanJobResponse,
  | 'id' | 'status' | 'scan_mode' | 'emails_listed' | 'emails_fetched'
  | 'emails_classified' | 'scanned_emails_size_bytes' | 'error_message'
  | 'total_classification_failures' | 'pause_reason' | 'limit_reached'
  | 'created_at' | 'started_at' | 'completed_at'
> & Partial<Omit<GetScanJobResponse, 'triggered_by'>> & { triggered_by: string };

export type ScanStatusState = {
  currentJob: ScanJobData | null
  lastCompletedJob: ScanJobData | null
  gmailStatus: AuthStatus | null
  isLoading: boolean
  error: string | null
  progress: number
  tier: 'free' | 'pro'
  needsFullScan?: boolean;
  emailsLimit: number | null
};

export type ResumeScanResult = {
  job_id: string
  status: 'IN_PROGRESS'
  resumed_at: string
  message: string
};

export type ScanStatusController = ScanStatusState & {
  startScan: (mode?: 'full' | 'delta') => Promise<{ job_id: string } | null>
  pauseScan: () => Promise<void>
  resumeScan: () => Promise<ResumeScanResult | null>
  cancelScan: () => Promise<void>
  refresh: () => Promise<ScanJobData | null>
};

/**
 * Poll cadence while a scan is running.
 *
 * Since #702 this actually runs for the full duration of a scan rather than
 * being switched off the moment Realtime reported `SUBSCRIBED`, so it is now
 * the dominant source of `GET /v1/scan/current` traffic.
 *
 * Resist tuning it on intuition. `scan.current` is a single indexed row read,
 * it emits no log lines (per-request access logging is disabled), and the
 * general rate limiter is effectively unbounded. Slowing it degrades the
 * progress UI the user is actively watching. If the rate ever needs reducing,
 * measure it first from Azure Container Apps HTTP metrics - see
 * docs/ERRORS_AND_LOGGING.md - and prefer fixing the Realtime `GRANT` so the
 * poller stands down naturally.
 */
export const POLL_INTERVAL_ACTIVE = 3000;
export const POLL_INTERVAL_IDLE = 60000;
export const STALE_THRESHOLD_MS = 60 * 60 * 1000;

const ACTIVE_STATUSES: ScanJobData['status'][] = ['PENDING', 'IN_PROGRESS', 'PAUSED'];
const TERMINAL_STATUSES: ScanJobData['status'][] = ['COMPLETED', 'FAILED', 'CANCELED'];

export function isActiveStatus(status: ScanJobData['status'] | null | undefined): boolean {
  return !!status && ACTIVE_STATUSES.includes(status);
}

export function isTerminalStatus(status: ScanJobData['status'] | null | undefined): boolean {
  return !!status && TERMINAL_STATUSES.includes(status);
}

type GmailConnectionSnapshot = { isConnected?: boolean | null; hasRequiredScopes?: boolean | null } | null | undefined;

/**
 * A Pro user who hasn't completed their post-upgrade unlimited full scan yet
 * (`needs_full_scan` from GET /subscription/status) should get one started
 * automatically - both on initial load and every subsequent idle poll -
 * instead of waiting on the staleness-based lazy refresh (which only fires
 * once an hour has passed since the last scan, and does nothing at all if
 * the user's prior free-tier scan was recent). Polling this on every idle
 * tick also covers the Stripe webhook sync lagging a few seconds behind the
 * checkout redirect: the very next poll picks up the tier flip.
 * See UC-2/UC-5 in docs/CORE_PRODUCT_GUIDELINES.md.
 */
export function shouldAutoStartFullScan(params: {
  tier: 'free' | 'pro';
  needsFullScan: boolean;
  autoEligible?: boolean;
  lastCompletionQuality?: GetScanJobResponse['completion_quality'];
  jobStatus: ScanJobData['status'] | null | undefined;
  gmailStatus: GmailConnectionSnapshot;
}): boolean {
  const { tier, needsFullScan, autoEligible, lastCompletionQuality, jobStatus, gmailStatus } = params;
  return (
    tier === 'pro' &&
    needsFullScan &&
    autoEligible !== false &&
    lastCompletionQuality !== 'partial' &&
    !isActiveStatus(jobStatus) &&
    !!gmailStatus?.isConnected &&
    gmailStatus?.hasRequiredScopes !== false
  );
}
